<template>
  <a-form layout="vertical" :model="formState" ref="formRef">
    <a-form-item label="Archivo de audio" name="file_audio" required>
      <ArchivePicker
        v-model:value="formState.file_audio"
        :supportedTypes="['.mp3']"
        :maxMBSize="3"
      />

      <AudioPreview
        v-if="formState.file_audio"
        :node-src="node?.file_audio_url"
        :file="formState.file_audio"
      />
    </a-form-item>

    <a-divider class="divider" />

    <a-form-item label="Nombre referencial del audio" name="referencial_name" required>
      <a-input
        v-model:value="formState.referencial_name"
        class="input"
        placeholder="Escribir nombre"
      />
      <p class="info">Te facilitará su visualización en el reporte del IVR</p>
    </a-form-item>

    <h4>
      Rango de números
      <a-tooltip
        title="El rango de números establece las opciones de calificación en la encuesta, determinado por el valor mínimo y máximo que configures"
      >
        <InfoCircleOutlined style="margin-left: 3px" />
      </a-tooltip>
    </h4>

    <div class="row">
      <a-form-item label="Desde" name="satisfaction_ranking_start" required>
        <a-input-number
          v-model:value="formState.satisfaction_ranking_start"
          class="input"
          placeholder="1"
          min="0"
          :max="formState.satisfaction_ranking_end - 1"
          :precision="0"
        />
      </a-form-item>

      <a-form-item label="Hasta" name="satisfaction_ranking_end" required>
        <a-input-number
          v-model:value="formState.satisfaction_ranking_end"
          class="input"
          placeholder="9"
          :min="(formState.satisfaction_ranking_start ?? 0) + 1"
          max="9"
          :precision="0"
        />
      </a-form-item>
    </div>

    <a-form-item label="Segundos a esperar en esta acción" name="time_waiting" required>
      <a-input-number
        v-model:value="formState.time_waiting"
        class="input"
        placeholder="30"
        min="0"
        :precision="0"
      />
      <p class="info">Se recomienda brindar un mínimo de 30 segundos al contacto</p>
    </a-form-item>

    <p class="message">
      Si supera el tiempo de espera, se procederá reproducir nuevamente el audio de esta acción.
    </p>

    <ActionPicker v-if="node" :node="node" :parentNode="parentNode" />
  </a-form>
</template>

<script setup lang="ts">
import { provide, reactive, ref } from "vue";
import { FormInstance } from "ant-design-vue";
import { InfoCircleOutlined } from "@ant-design/icons-vue";

import { CreateNodeBody, Node } from "@/@types/ivr";
import { DrawerFormStateKey } from "@/@types/ivr/symbols";

import ArchivePicker from "@/app/shared/components/molecules/ArchivePicker.vue";
import ActionPicker from "@/app/ivr/bot/components/pickers/ActionPicker.vue";
import AudioPreview from "../../buttons/AudioPreview.vue";

const props = defineProps<{ node?: Node; parentNode?: Node }>();

const formRef = ref<FormInstance>();
const formState = reactive<Partial<CreateNodeBody>>({
  file_audio: props.node?.file_audio,
  satisfaction_ranking_start: props.node?.satisfaction_ranking_start,
  satisfaction_ranking_end: props.node?.satisfaction_ranking_end,
  referencial_name: props.node?.referencial_name,
  end_call_file_audio: props.node?.end_call_file_audio,
  time_waiting: props.node?.time_waiting,
});

provide(DrawerFormStateKey, formState);

defineExpose({ formState, formRef });
</script>

<style scoped lang="sass">
h4
  margin-bottom: 8px
  font-size: 14px
  font-weight: 600
  color: $gray-8

p
  margin: 0
  color: $gray-8

.audio-preview
  width: 100%
  height: 32px
  margin-top: 10px

.divider
  margin: 0
  margin-bottom: 24px
  background-color: $gray-4

.info
  color: $gray-7

.message
  margin-bottom: 24px

.input
  width: 100%

.row
  display: flex
  align-items: center
  gap: 20px

  > .ant-form-item
    flex: 1 1 100%
</style>
